/**
 * Hook de recherche globale sur toutes les données d'un audit complet.
 * Charge les données paresseusement à la première ouverture.
 *
 * Améliorations v2 :
 *   - Score de pertinence : exact > commence-par > mot entier > contient
 *   - Erreurs groupées par type (plus lisible que page par page)
 *   - Catégorie « Infos du site » (CMS, hébergeur, stack…) via projectData
 *   - Résultats triés par pertinence décroissante
 */
import { useState, useCallback, useRef } from 'react';
import {
  getAudits,
  getTechnicalErrors,
  getAIRecommendations,
  getPositioningAnalyses,
} from '../api';

// ── Normalisation & score de pertinence ───────────────────────────────────────

function norm(str) {
  return (str || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Retourne un score de pertinence 0-100 entre un champ et une requête.
 * 100 = correspondance exacte, 80 = commence par, 70 = début de mot, 50 = contient.
 */
function relevance(field, q) {
  const f = norm(field);
  const nq = norm(q);
  if (!f || !nq) return 0;
  if (f === nq) return 100;
  if (f.startsWith(nq)) return 80;
  // Début de mot (précédé d'un espace, /, - ou _)
  if (/[\s/\-_]/.test(f[f.indexOf(nq) - 1] || ' ')) return 70;
  if (f.includes(nq)) return 50;
  return 0;
}

function bestRelevance(fields, q) {
  return Math.max(0, ...fields.map((f) => relevance(f, q)));
}

function match(str, q) {
  return norm(str).includes(norm(q));
}

function matchAny(fields, q) {
  return fields.some((f) => match(f, q));
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export default function useProjectSearch(slug, projectData = null) {
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(false);
  const loadedRef = useRef(false);

  const load = useCallback(async () => {
    if (loadedRef.current || !slug) return;
    setLoading(true);
    try {
      const [auditsRes, errorsRes, recoRes, posRes] = await Promise.allSettled([
        getAudits(slug),
        getTechnicalErrors(slug),
        getAIRecommendations(slug),
        getPositioningAnalyses(slug),
      ]);
      loadedRef.current = true;
      setRawData({
        audits: auditsRes.status === 'fulfilled' ? (auditsRes.value || []) : [],
        errors: errorsRes.status === 'fulfilled' ? (errorsRes.value || {}) : {},
        reco: recoRes.status === 'fulfilled' ? (recoRes.value || {}) : {},
        positioning: posRes.status === 'fulfilled' ? (posRes.value?.[0] || {}) : {},
      });
    } catch (e) {
      console.warn('[useProjectSearch] load error', e);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  /**
   * Recherche et retourne des résultats groupés, triés par pertinence.
   */
  const search = useCallback(
    (query) => {
      const q = query?.trim();
      if (!rawData || !q) return null;

      const pages = [];
      const errors = [];
      const recommendations = [];
      const positioning = [];
      const siteInfo = [];

      // ── Pages auditées — triées par pertinence ───────────────────────────
      for (const a of rawData.audits) {
        const score = bestRelevance([a.url, a.title, a.meta_description, a.h1], q);
        if (score > 0) {
          pages.push({ ...a, _score: score });
        }
      }
      // Trier par pertinence desc, puis par score_global desc
      pages.sort((a, b) => b._score - a._score || (b.score_global || 0) - (a.score_global || 0));
      // Limiter à 15
      const topPages = pages.slice(0, 15).map((a) => ({
        type: 'page',
        url: a.url,
        title: a.title || a.url,
        subtitle: a.meta_description || a.h1 || '',
        score: a.score_global,
        scorePerf: a.pagespeed_average,
      }));

      // ── Erreurs groupées par type ────────────────────────────────────────
      // Construire l'index type → {count, severity, message, pages}
      const errorTypeIndex = new Map();
      const errorPages = rawData.errors?.errors || [];
      for (const page of errorPages) {
        for (const err of page.errors || []) {
          const key = err.type || err.message || 'unknown';
          if (!errorTypeIndex.has(key)) {
            errorTypeIndex.set(key, {
              type: key,
              message: err.message || err.description || '',
              count: 0,
              severity: err.severity,
              examplePages: [],
            });
          }
          const entry = errorTypeIndex.get(key);
          entry.count++;
          if (entry.examplePages.length < 2) entry.examplePages.push(page.url);
        }
      }

      const errorResults = [];
      for (const [type, entry] of errorTypeIndex) {
        const score = bestRelevance([type, entry.message], q);
        if (score > 0) {
          errorResults.push({
            type: 'error',
            title: entry.message || type,
            subtitle: `${entry.count} page${entry.count > 1 ? 's' : ''} affectée${entry.count > 1 ? 's' : ''}`,
            severity: entry.severity,
            errorType: type,
            _score: score,
          });
        }
      }
      // Also search by URL if no type matched
      if (errorResults.length === 0) {
        for (const page of errorPages) {
          if (match(page.url, q)) {
            errorResults.push({
              type: 'error',
              title: page.url.replace(/^https?:\/\//, ''),
              subtitle: `${page.errors?.length || 0} erreur${page.errors?.length > 1 ? 's' : ''}`,
              severity: page.errors?.[0]?.severity,
              errorType: null,
              _score: 50,
            });
          }
        }
      }
      errorResults.sort((a, b) => b._score - a._score);

      // ── Recommandations IA ───────────────────────────────────────────────
      const recoResults = [];
      const allRecos = [
        ...(rawData.reco?.priority_recommendations || []),
        ...(rawData.reco?.action_plan || []),
        ...(rawData.reco?.recommendations || []),
      ];
      const seenRecos = new Set();
      for (const r of allRecos) {
        const title = r.title || r.tache || (typeof r === 'string' ? r : String(r));
        if (seenRecos.has(title)) continue;
        const score = bestRelevance([title, r.description || '', r.category || '', r.impact || ''], q);
        if (score > 0) {
          seenRecos.add(title);
          recoResults.push({
            type: 'recommendation',
            title,
            subtitle: r.description || r.category || '',
            impact: r.impact || r.priority,
            _score: score,
          });
        }
      }
      recoResults.sort((a, b) => b._score - a._score);

      // ── Positionnement ───────────────────────────────────────────────────
      const pos = rawData.positioning;
      const posGroups = [
        { key: 'points_forts', label: 'Point fort' },
        { key: 'points_faibles', label: 'Opportunité' },
        { key: 'recommandations', label: 'Recommandation' },
        { key: 'todo_list', label: 'Action' },
        { key: 'mots_cles', label: 'Mot-clé' },
        { key: 'concurrents', label: 'Concurrent' },
        { key: 'clientele', label: 'Clientèle' },
        { key: 'questions_clients', label: 'Question client' },
      ];
      for (const { key, label } of posGroups) {
        const items = pos[key];
        if (!items) continue;
        const arr = Array.isArray(items) ? items : [items];
        for (const item of arr) {
          const text = typeof item === 'string'
            ? item
            : (item?.text || item?.title || item?.name || item?.kw || JSON.stringify(item));
          const score = relevance(text, q);
          if (score > 0) {
            positioning.push({ type: 'positioning', title: text, subtitle: label, posKey: key, _score: score });
          }
        }
      }
      // Champs scalaires
      for (const [key, label] of [['secteur', 'Secteur'], ['comprehension', 'Compréhension IA'], ['localisation', 'Localisation']]) {
        if (pos[key]) {
          const score = relevance(pos[key], q);
          if (score > 0) positioning.push({ type: 'positioning', title: pos[key], subtitle: label, posKey: key, _score: score });
        }
      }
      positioning.sort((a, b) => b._score - a._score);

      // ── Infos du site (données synchrones du projet) ─────────────────────
      if (projectData) {
        const fields = [
          { label: 'CMS', value: projectData.cms },
          { label: 'Hébergeur', value: projectData.hosting },
          { label: 'Technologie', value: projectData.technology || projectData.framework },
          { label: 'CDN', value: projectData.cdn },
          { label: 'Outils marketing', value: projectData.marketing_tools },
          { label: 'Secteur d\'activité', value: projectData.secteur },
          { label: 'Serveur', value: projectData.server },
        ];
        for (const { label, value } of fields) {
          if (!value) continue;
          const score = relevance(value, q);
          if (score > 0) {
            siteInfo.push({ type: 'siteInfo', title: value, subtitle: label, _score: score });
          }
        }
        siteInfo.sort((a, b) => b._score - a._score);
      }

      return {
        pages: topPages,
        errors: errorResults.slice(0, 15),
        recommendations: recoResults.slice(0, 12),
        positioning: positioning.slice(0, 12),
        siteInfo: siteInfo.slice(0, 6),
      };
    },
    [rawData, projectData]
  );

  return { load, search, loading, ready: !!rawData };
}
